import { useState } from "react";
import { Eye, Loader2, Save } from "lucide-react";
import { audienciasApi, type PreviewAudiencia } from "../../lib/api";
import type { Criterios, ModoConsumo, NivelConsumo } from "./criterios";

const RFM_OPCIONES = [
  { v: "", t: "Cualquiera" },
  { v: "vip", t: "VIP" },
  { v: "recurrentes", t: "Recurrentes" },
  { v: "en_riesgo", t: "En riesgo" },
  { v: "nuevos", t: "Nuevos" },
  { v: "ocasionales", t: "Ocasionales" },
];
const MODOS: { v: ModoConsumo; t: string }[] = [
  { v: "compro", t: "Compró" },
  { v: "dejo", t: "Dejó de comprar" },
  { v: "nunca", t: "Nunca compró" },
];
const NIVELES: { v: NivelConsumo; t: string }[] = [
  { v: "familia", t: "Familia" },
  { v: "seccion", t: "Sección" },
  { v: "departamento", t: "Departamento" },
  { v: "articulo", t: "Artículo (código)" },
];

const num = (s: string): number | undefined => (s.trim() === "" ? undefined : Number(s));

/** Convierte el estado del formulario en el objeto Criterios que viaja al back. */
export function construirCriterios(f: EstadoForm): Criterios {
  const c: Criterios = {};
  if (f.rfm) c.rfm = f.rfm;
  if (f.churn_dias !== undefined) c.churn_dias = f.churn_dias;
  if (f.gasto_min !== undefined) c.gasto_min = f.gasto_min;
  if (f.ltv_min !== undefined) c.ltv_min = f.ltv_min;
  if (f.ticket_min !== undefined) c.ticket_min = f.ticket_min;
  if (f.ticket_max !== undefined) c.ticket_max = f.ticket_max;
  if (f.visitas_min !== undefined) c.visitas_min = f.visitas_min;
  if (f.sexo) c.sexo = f.sexo;
  if (f.edad_min !== undefined) c.edad_min = f.edad_min;
  if (f.edad_max !== undefined) c.edad_max = f.edad_max;
  if (f.cumple_mes !== undefined) c.cumple_mes = f.cumple_mes;
  if (f.zona) c.zona = f.zona;
  if (f.compra_promo) c.compra_promo = f.compra_promo as Criterios["compra_promo"];
  if (f.consumo_valor.trim()) {
    c.consumo = {
      modo: f.consumo_modo, nivel: f.consumo_nivel, valor: f.consumo_valor.trim(),
      min_unidades: f.consumo_min_unidades, min_gasto: f.consumo_min_gasto,
      ventana_dias: f.consumo_ventana, inactivo_dias: f.consumo_inactivo,
    };
  }
  return c;
}

export interface EstadoForm {
  rfm: string; churn_dias?: number; gasto_min?: number; ltv_min?: number;
  ticket_min?: number; ticket_max?: number; visitas_min?: number;
  sexo: string; edad_min?: number; edad_max?: number; cumple_mes?: number; zona: string;
  compra_promo: string;
  consumo_modo: ModoConsumo; consumo_nivel: NivelConsumo; consumo_valor: string;
  consumo_min_unidades?: number; consumo_min_gasto?: number; consumo_ventana?: number; consumo_inactivo?: number;
}

const FORM_VACIO: EstadoForm = {
  rfm: "", sexo: "", zona: "", compra_promo: "",
  consumo_modo: "compro", consumo_nivel: "familia", consumo_valor: "",
};

const campo = "w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none";
const etiqueta = "block text-xs font-medium text-ink-500 mb-1";

export function ConstructorCriterios({ org, onGuardar }: { org?: number; onGuardar: (nombre: string, criterios: Criterios) => void }) {
  const [f, setF] = useState<EstadoForm>(FORM_VACIO);
  const [nombre, setNombre] = useState("");
  const [vista, setVista] = useState<PreviewAudiencia | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof EstadoForm>(k: K, v: EstadoForm[K]) => setF((x) => ({ ...x, [k]: v }));

  async function previsualizar() {
    setError(null); setCargando(true); setVista(null);
    try {
      setVista(await audienciasApi.preview(construirCriterios(f), org));
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo previsualizar");
    } finally {
      setCargando(false);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <label className={etiqueta}>Nombre del segmento (opcional)</label>
        <input className={campo} value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej.: VIP que dejó la pizza" />
      </div>

      <fieldset className="rounded-xl border border-ink-100 p-4">
        <legend className="px-2 text-xs font-semibold text-ink-600">Valor y comportamiento</legend>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div>
            <label className={etiqueta}>Clase RFM</label>
            <select className={campo} value={f.rfm} onChange={(e) => set("rfm", e.target.value)}>
              {RFM_OPCIONES.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
            </select>
          </div>
          <div><label className={etiqueta}>Sin volver (días) ≥</label>
            <input className={campo} type="number" onChange={(e) => set("churn_dias", num(e.target.value))} /></div>
          <div><label className={etiqueta}>Gasto USD ≥</label>
            <input className={campo} type="number" step="0.01" onChange={(e) => set("gasto_min", num(e.target.value))} /></div>
          <div><label className={etiqueta}>LTV USD ≥</label>
            <input className={campo} type="number" step="0.01" onChange={(e) => set("ltv_min", num(e.target.value))} /></div>
          <div><label className={etiqueta}>Ticket USD ≥</label>
            <input className={campo} type="number" step="0.01" onChange={(e) => set("ticket_min", num(e.target.value))} /></div>
          <div><label className={etiqueta}>Ticket USD ≤</label>
            <input className={campo} type="number" step="0.01" onChange={(e) => set("ticket_max", num(e.target.value))} /></div>
          <div><label className={etiqueta}>Visitas ≥</label>
            <input className={campo} type="number" onChange={(e) => set("visitas_min", num(e.target.value))} /></div>
          <div>
            <label className={etiqueta}>Comportamiento de precio</label>
            <select className={campo} value={f.compra_promo} onChange={(e) => set("compra_promo", e.target.value)}>
              <option value="">Cualquiera</option>
              <option value="solo_promo">Solo promo</option>
              <option value="solo_full">Solo full</option>
              <option value="mixto">Mixto</option>
            </select>
          </div>
        </div>
      </fieldset>

      <fieldset className="rounded-xl border border-ink-100 p-4">
        <legend className="px-2 text-xs font-semibold text-ink-600">Consumo de producto</legend>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div>
            <label className={etiqueta}>Modo</label>
            <select className={campo} value={f.consumo_modo} onChange={(e) => set("consumo_modo", e.target.value as ModoConsumo)}>
              {MODOS.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
            </select>
          </div>
          <div>
            <label className={etiqueta}>Nivel</label>
            <select className={campo} value={f.consumo_nivel} onChange={(e) => set("consumo_nivel", e.target.value as NivelConsumo)}>
              {NIVELES.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
            </select>
          </div>
          <div className="col-span-2"><label className={etiqueta}>Valor (nombre o código)</label>
            <input className={campo} value={f.consumo_valor} onChange={(e) => set("consumo_valor", e.target.value)} placeholder="Ej.: PIZZA" /></div>
          <div><label className={etiqueta}>Mín. unidades</label>
            <input className={campo} type="number" onChange={(e) => set("consumo_min_unidades", num(e.target.value))} /></div>
          <div><label className={etiqueta}>Mín. gasto USD</label>
            <input className={campo} type="number" step="0.01" onChange={(e) => set("consumo_min_gasto", num(e.target.value))} /></div>
          <div><label className={etiqueta}>Ventana (días)</label>
            <input className={campo} type="number" onChange={(e) => set("consumo_ventana", num(e.target.value))} /></div>
          <div><label className={etiqueta}>Inactivo (días)</label>
            <input className={campo} type="number" onChange={(e) => set("consumo_inactivo", num(e.target.value))} /></div>
        </div>
      </fieldset>

      <fieldset className="rounded-xl border border-ink-100 p-4">
        <legend className="px-2 text-xs font-semibold text-ink-600">Demografía</legend>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div>
            <label className={etiqueta}>Sexo</label>
            <select className={campo} value={f.sexo} onChange={(e) => set("sexo", e.target.value)}>
              <option value="">Cualquiera</option><option value="M">Hombres</option><option value="F">Mujeres</option>
            </select>
          </div>
          <div><label className={etiqueta}>Edad ≥</label>
            <input className={campo} type="number" onChange={(e) => set("edad_min", num(e.target.value))} /></div>
          <div><label className={etiqueta}>Edad ≤</label>
            <input className={campo} type="number" onChange={(e) => set("edad_max", num(e.target.value))} /></div>
          <div><label className={etiqueta}>Cumple en mes (1-12)</label>
            <input className={campo} type="number" min={1} max={12} onChange={(e) => set("cumple_mes", num(e.target.value))} /></div>
          <div className="col-span-2"><label className={etiqueta}>Zona</label>
            <input className={campo} value={f.zona} onChange={(e) => set("zona", e.target.value)} /></div>
        </div>
      </fieldset>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {vista && (
        <div className="rounded-xl bg-ink-50 p-4 text-sm">
          <p className="font-semibold text-ink-800">{vista.total.toLocaleString()} clientes</p>
          <p className="text-ink-500">{vista.con_opt_in.toLocaleString()} de {vista.total.toLocaleString()} aceptan recibir info</p>
        </div>
      )}

      <div className="flex gap-2">
        <button onClick={previsualizar} disabled={cargando}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
          {cargando ? <Loader2 className="size-4 animate-spin" /> : <Eye className="size-4" />} Vista previa
        </button>
        <button onClick={() => onGuardar(nombre, construirCriterios(f))}
          className="inline-flex items-center gap-2 rounded-lg border border-ink-200 px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50">
          <Save className="size-4" /> Guardar segmento
        </button>
      </div>
    </div>
  );
}
